import {inject, Injectable} from '@angular/core';
import {lastValueFrom} from 'rxjs';
import {injectMutation, QueryClient} from '@tanstack/angular-query-experimental';
import {
  ChangeTodoStatusRequest,
  CreateTodoRequest,
  CreateTodoStatusRequest,
  GetTodosResponse,
  ReorderTodoStatusesRequest,
  TodoCompletion,
  TodoService,
  TodoStatusService,
  TodoStatusSummary,
  TodoSummary,
  UpdateTodoRequest,
  UpdateTodoStatusRequest,
} from '../../../../api';
import {TodoFormValue} from '../models/todo-form-value';
import {QueryKey} from '../../../constants/query-key';
import {TodoCompletionService} from './todo-completion.service';
import {TodoQueryService} from './todo-query.service';

export interface UploadTodoDocumentInput {
  todoId: number;
  file: File;
}

export interface DeleteTodoDocumentInput {
  todoId: number;
  documentId: number;
}

@Injectable()
export class TodoCommandService {

  private readonly todoService = inject(TodoService);
  private readonly todoStatusService = inject(TodoStatusService);
  private readonly completionService = inject(TodoCompletionService);
  private readonly queryService = inject(TodoQueryService);
  private readonly queryClient = inject(QueryClient);

  createTodoMutation = injectMutation(() => ({
    mutationFn: (request: CreateTodoRequest) =>
      lastValueFrom(this.todoService.createTodo(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODOS]});
    },
  }));

  updateTodoMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateTodoRequest }) =>
      lastValueFrom(this.todoService.updateTodo(request.id, request.body)),
    onSuccess: (_, req) => {
      this.completionService.invalidateAll();
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO, req.id]});
    },
  }));

  createTodoStatusMutation = injectMutation(() => ({
    mutationFn: (request: CreateTodoStatusRequest) =>
      lastValueFrom(this.todoStatusService.createTodoStatus(request)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODOS]}),
        this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUSES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUS_SUMMARIES]});
    }
  }));

  updateTodoStatusMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; body: UpdateTodoStatusRequest }) =>
      lastValueFrom(this.todoStatusService.updateTodoStatus(request.id, request.body)),
    onSuccess: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUSES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUS_SUMMARIES]});
    },
  }));

  deleteTodoStatusMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.todoStatusService.deleteTodoStatus(id)),
    onSuccess: () => {
      this.completionService.invalidateAll();
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUSES]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUS_SUMMARIES]});
    },
  }));

  reorderTodoStatusesMutation = injectMutation(() => ({
    mutationFn: (request: ReorderTodoStatusesRequest) =>
      lastValueFrom(this.todoStatusService.reorderTodoStatuses(request)),
    onMutate: (request: ReorderTodoStatusesRequest) => {
      const queryKey = [QueryKey.TODO_STATUSES];
      this.queryClient.cancelQueries({queryKey});
      const previousStatuses = this.queryClient.getQueryData<{ todoStatuses: TodoStatusSummary[] }>(queryKey);

      this.queryClient.setQueryData<{ todoStatuses: TodoStatusSummary[] }>(queryKey, (old) => {
        if (!old?.todoStatuses) return old;

        const sorted = [...old.todoStatuses].sort((a, b) => (a.orderNum ?? 0) - (b.orderNum ?? 0));
        const currentIndex = sorted.findIndex(status => status.id === request.statusId);
        if (currentIndex === -1) return old;

        const [moved] = sorted.splice(currentIndex, 1);
        const insertAt = Math.min(Math.max(request.newOrderNumber, 0), sorted.length);
        sorted.splice(insertAt, 0, moved);

        const reorderedStatuses = sorted.map((status, index) => ({...status, orderNum: index}));
        return {todoStatuses: reorderedStatuses};
      });

      return {previousStatuses, queryKey};
    },
    onError: (_err: unknown, _request: ReorderTodoStatusesRequest, context: {
      previousStatuses: { todoStatuses: TodoStatusSummary[] } | undefined;
      queryKey: unknown[];
    } | undefined) => {
      if (context?.previousStatuses !== undefined) {
        this.queryClient.setQueryData(context.queryKey, context.previousStatuses);
      }
    },
    onSettled: () => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO_STATUS_SUMMARIES]});
    },
  }));

  changeTodoStatusMutation = injectMutation(() => ({
    mutationFn: (request: {
      id: number;
      statusId: ChangeTodoStatusRequest
    }) => lastValueFrom(this.todoService.changeTodoStatus(request.id, request.statusId)),

    onMutate: (request: { id: number; statusId: ChangeTodoStatusRequest }) => {
      const queryKey = [QueryKey.TODOS, this.queryService.filterParams()];

      this.queryClient.cancelQueries({queryKey});
      const previousTodos = this.queryClient.getQueryData<GetTodosResponse>(queryKey);

      const statusesData = this.queryClient.getQueryData<{
        todoStatuses: TodoStatusSummary[]
      }>([QueryKey.TODO_STATUSES]);

      const targetStatus = statusesData?.todoStatuses?.find(s => s.id === request.statusId.statusId);

      this.queryClient.setQueryData<GetTodosResponse>(queryKey, (old) => {
        if (!old?.todos) return old;

        const movedTodo = old.todos.find(t => t.id === request.id);
        if (!movedTodo || !targetStatus) return old;

        const oldStatusId = movedTodo.status?.id;
        const newStatusId = request.statusId.statusId;
        const insertAt = request.statusId.orderNum ?? 0;

        const targetColumnTodos = old.todos
          .filter(t => t.status?.id === newStatusId && t.id !== request.id)
          .sort((a, b) => (a.orderNum ?? 0) - (b.orderNum ?? 0));
        targetColumnTodos.splice(insertAt, 0, movedTodo);

        const orderMap = new Map<number, number>();
        targetColumnTodos.forEach((t, i) => orderMap.set(t.id!, i));

        if (oldStatusId !== newStatusId) {
          old.todos
            .filter(t => t.status?.id === oldStatusId && t.id !== request.id)
            .sort((a, b) => (a.orderNum ?? 0) - (b.orderNum ?? 0))
            .forEach((t, i) => orderMap.set(t.id!, i));
        }

        return {
          ...old,
          todos: old.todos.map((todo: TodoSummary) => {
            if (todo.id === request.id) {
              return {...todo, status: targetStatus, orderNum: insertAt};
            }
            const newOrder = orderMap.get(todo.id!);
            if (newOrder !== undefined) {
              return {...todo, orderNum: newOrder};
            }
            return todo;
          }),
        };
      });

      return {previousTodos, queryKey};
    },
    onError: (_err: unknown, _request: { id: number; statusId: ChangeTodoStatusRequest }, context: {
      previousTodos: GetTodosResponse | undefined;
      queryKey: unknown[];
    } | undefined) => {
      if (context?.previousTodos !== undefined) {
        this.queryClient.setQueryData(context.queryKey, context.previousTodos);
      }
    },
    onSuccess: () => {
      this.completionService.invalidateAll();
    },
  }));

  changeTodoCompletionMutation = injectMutation(() => ({
    mutationFn: (request: { id: number; completion: TodoCompletion }) =>
      this.completionService.changeTodoCompletion(request.id, request.completion),
    onMutate: (request: { id: number; completion: TodoCompletion }) => {
      const queryKey = [QueryKey.TODOS, this.queryService.filterParams()];
      this.queryClient.cancelQueries({queryKey});
      const previousTodos = this.queryClient.getQueryData<GetTodosResponse>(queryKey);
      this.queryClient.setQueryData<GetTodosResponse>(queryKey, (old) => {
        if (!old?.todos) return old;
        return {
          ...old,
          todos: old.todos.map(todo =>
            todo.id === request.id ? {...todo, completion: request.completion} : todo
          ),
        };
      });
      return {previousTodos, queryKey};
    },
    onError: (_err: unknown, _request: { id: number; completion: TodoCompletion }, context: {
      previousTodos: GetTodosResponse | undefined;
      queryKey: unknown[];
    } | undefined) => {
      if (context?.previousTodos !== undefined) {
        this.queryClient.setQueryData(context.queryKey, context.previousTodos);
      }
    },
    onSuccess: () => {
      this.completionService.invalidateAll();
    },
  }));

  deleteTodoMutation = injectMutation(() => ({
    mutationFn: (id: number) => lastValueFrom(this.todoService.deleteTodo(id)),
    onSuccess: () => {
      this.completionService.invalidateAll();
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]});
    },
  }));

  uploadTodoDocumentMutation = injectMutation(() => ({
    mutationFn: (req: UploadTodoDocumentInput) =>
      lastValueFrom(this.todoService.uploadTodoDocument(req.todoId, req.file)),
    onSuccess: (_, req) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO, req.todoId]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]});
    },
  }));

  deleteTodoDocumentMutation = injectMutation(() => ({
    mutationFn: (req: DeleteTodoDocumentInput) =>
      lastValueFrom(this.todoService.deleteTodoDocument(req.todoId, req.documentId)),
    onSuccess: (_, req) => {
      this.queryClient.invalidateQueries({queryKey: [QueryKey.TODO, req.todoId]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.FOLDERS]});
      this.queryClient.invalidateQueries({queryKey: [QueryKey.DOCUMENTS]});
    },
  }));

  async saveTodo(params: {
    selectedId: number | null;
    value: TodoFormValue;
    pendingFiles: File[];
    pendingDeletionIds: number[];
  }): Promise<void> {
    const {selectedId, value, pendingFiles, pendingDeletionIds} = params;

    if (selectedId !== null) {
      await Promise.all([
        this.updateTodoMutation.mutateAsync({
          id: selectedId,
          body: {
            title: value.title!,
            description: value.description,
            dueDate: value.dueDate ?? undefined,
            concernId: value.concernId ?? undefined,
            priorityName: value.priorityName ?? undefined,
            estimatedTimeInDays: value.estimatedTimeInDays ?? null,
            statusId: value.statusId ?? undefined,
            isPrivate: value.isPrivate ?? false,
            assignedToIds: value.assignedToIds ?? undefined,
            completion: this.toCompletion(value.isDone),
            children: value.children?.map(s => ({
              id: s.id ?? undefined,
              title: s.title,
              dueDate: s.dueDate ?? undefined,
              assignedToId: s.assignedToId ?? undefined,
              completion: this.toCompletion(s.isDone),
            })),
          },
        }),
        ...pendingFiles.map(file =>
          this.uploadTodoDocumentMutation.mutateAsync({todoId: selectedId, file})
        ),
        ...pendingDeletionIds.map(documentId =>
          this.deleteTodoDocumentMutation.mutateAsync({todoId: selectedId, documentId})
        ),
      ]);
    } else {
      const result = await this.createTodoMutation.mutateAsync({
        title: value.title!,
        description: value.description,
        dueDate: value.dueDate,
        concernId: value.concernId,
        priorityName: value.priorityName,
        estimatedTimeInDays: value.estimatedTimeInDays,
        statusId: value.statusId,
        isPrivate: value.isPrivate ?? false,
        assignedToIds: value.assignedToIds ?? [],
        children: (value.children ?? []).map(s => ({
          title: s.title,
          dueDate: s.dueDate,
          assignedToId: s.assignedToId,
          completion: this.toCompletion(s.isDone),
        })),
        completion: this.toCompletion(value.isDone),
      });
      if (pendingFiles.length > 0) {
        await Promise.all(
          pendingFiles.map(file =>
            this.uploadTodoDocumentMutation.mutateAsync({todoId: result.id, file})
          )
        );
      }
    }
  }

  private toCompletion(isDone: boolean | null): TodoCompletion {
    return isDone ? TodoCompletion.DONE : TodoCompletion.PENDING;
  }
}
